<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useElementSize } from '@vueuse/core';
import BottomSheet, { type BottomSheetPosition } from '@/components/layout/BottomSheet.vue';
import ActionButton from '@/components/ui/ActionButton.vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import ItineraryField from './ItineraryField.vue';
import JourneyCard from './JourneyCard.vue';
import JourneyTracking from './JourneyTracking.vue';
import MapPointPicker from './MapPointPicker.vue';
import PlacePicker from './PlacePicker.vue';
import TimeSelector from './TimeSelector.vue';
import { AppRoute } from '@/libs/routing';
import { parsePlaceParam, placeParams, type Place } from '@/libs/journey/places';
import type { AddressResult } from '@/libs/geocoding';
import { MapFocusType, useAppStore } from '@/stores/app';
import { useDevicesStore } from '@/stores/devices';
import { useGtfsStore } from '@/stores/gtfs';
import { useJourneyStore, type TimeMode } from '@/stores/journey';

const { t, d, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const devicesStore = useDevicesStore();
const gtfsStore = useGtfsStore();
const journeyStore = useJourneyStore();

const sheetPosition = ref<BottomSheetPosition>('intermediate');
const topEl = ref<HTMLElement>();
const { height: topHeight } = useElementSize(topEl);

const picking = ref<'origin' | 'destination'>();
/** The end being picked on the map, the picker closed meanwhile. */
const pickingOnMap = ref<'origin' | 'destination'>();
const isTimeSelectorOpen = ref<boolean>(false);

// #region route query: ?from=<stopId|lat,lon>&to=<stopId|lat,lon>, with ?fromLabel= / ?toLabel= for a point
const queryParam = (value: unknown): string | undefined =>
  (Array.isArray(value) ? value[0] : value)?.toString() || undefined;

watch(
  () => [route.query.from, route.query.to, route.query.fromLabel, route.query.toLabel],
  () => {
    if (route.name !== AppRoute.ITINERARY) return;
    const { from, to, fromLabel, toLabel } = route.query;
    journeyStore.setOrigin(parsePlaceParam(queryParam(from), queryParam(fromLabel)) ?? { kind: 'position' });
    journeyStore.setDestination(parsePlaceParam(queryParam(to), queryParam(toLabel)));
  },
  { immediate: true },
);

function updateQuery(origin: Place, destination: Place | undefined) {
  const from = placeParams(origin);
  const to = placeParams(destination);
  router.replace({
    name: AppRoute.ITINERARY,
    query: { from: from.value, fromLabel: from.label, to: to.value, toLabel: to.label },
  });
}
// #endregion

const stopName = (stopId: string) => gtfsStore.gtfsData.stops[stopId]?.stop_name ?? stopId;

function placeLabel(place: Place): string {
  if (place.kind === 'stop') return stopName(place.stopId);
  if (place.kind === 'point') return place.label || t('itinerary.pointOnMap');
  return t('itinerary.yourPosition');
}

const originLabel = computed<string>(() => placeLabel(journeyStore.origin));
const destinationLabel = computed<string | undefined>(
  () => journeyStore.destination && placeLabel(journeyStore.destination),
);

const timeLabel = computed<string>(() => {
  if (journeyStore.timeMode === 'now') return t('itinerary.time.now');
  const time = journeyStore.chosenTime * 1000;
  const sameDay = new Date(time).toDateString() === new Date().toDateString();
  const day = sameDay
    ? ''
    : ` · ${new Intl.DateTimeFormat(locale.value, { weekday: 'short', day: 'numeric', month: 'short' }).format(time)}`;
  return `${t(`itinerary.time.${journeyStore.timeMode}`)} ${d(time, 'timeShort')}${day}`;
});

function onPick(place: Place, end = picking.value) {
  if (end === 'origin') updateQuery(place, journeyStore.destination);
  else updateQuery(journeyStore.origin, place);
  picking.value = undefined;
}

/** The map opens on the whole network, whatever end was chosen before or wherever the passenger stands. */
function startPickingOnMap() {
  const end = picking.value;
  picking.value = undefined;
  pickingOnMap.value = end;
  // Before any framing: a pick is framed without the camera padding of the sheet.
  appStore.setPickingOnMap(true);
  appStore.setMapBottomInset(0, false);
  appStore.setMapFocus({ type: MapFocusType.BOUNDS, elem: gtfsStore.networkMapBounds });
}

/** The pick left the map on the whole network: back to both ends. */
function endPickingOnMap() {
  pickingOnMap.value = undefined;
  frameEnds();
}

function onMapPointConfirm(place: Place) {
  const end = pickingOnMap.value;
  const current = end === 'origin' ? journeyStore.origin : journeyStore.destination;
  const unchanged = JSON.stringify(placeParams(place)) === JSON.stringify(placeParams(current));
  onPick(place, end);
  // A new end is framed by the ends watcher once the route has set it, not before.
  if (unchanged) endPickingOnMap();
  else pickingOnMap.value = undefined;
}

function onPickAddress(address: AddressResult) {
  onPick({ kind: 'point', lat: address.lat, lon: address.lon, label: address.name });
}

/** The passenger's position cannot be an arrival: swapped from it, the arrival is left to pick. */
function swap() {
  const { origin, destination } = journeyStore;
  updateQuery(destination ?? { kind: 'position' }, origin.kind === 'position' ? undefined : origin);
}

function close() {
  if (router.options.history.state.back) router.back();
  else router.replace({ name: AppRoute.MAP });
}

function onTimeApply(mode: TimeMode, time: number) {
  journeyStore.setTime(mode, time);
  isTimeSelectorOpen.value = false;
}

const routes = computed(() => gtfsStore.gtfsData.routes);
const journeys = computed(() => journeyStore.results.journeys);

// The vehicles of the lines on offer give the next search their estimated times.
watch(
  () => [
    ...new Set(
      journeys.value.flatMap(j => j.legs.flatMap(leg => (leg.type === 'transit' ? [leg.routeId] : []))),
    ),
  ],
  (routeIds, previous) => {
    if (routeIds.join() === previous?.join()) return;
    if (routeIds.length > 0) devicesStore.startGetDevices({ routeIds });
    else if (previous?.length) devicesStore.stopGetDevices();
  },
);

/** About 300 m around the ends: a lone end or two close ones are not framed at street level. */
const END_FRAME_MARGIN_DEG = 0.003;

function placePoint(place: Place | undefined): { lat: number; lon: number } | undefined {
  if (place?.kind === 'point') return place;
  if (place?.kind === 'position') return appStore.positionGeolocation;
  const stop = place?.kind === 'stop' ? gtfsStore.gtfsData.stops[place.stopId] : undefined;
  return stop && { lat: stop.stop_lat, lon: stop.stop_lon };
}

/** Frames the departure and the arrival chosen, or the whole network while none is known. */
function frameEnds() {
  const points = [journeyStore.origin, journeyStore.destination]
    .map(placePoint)
    .filter((point): point is { lat: number; lon: number } => !!point);
  if (points.length === 0) {
    appStore.setMapFocus({ type: MapFocusType.BOUNDS, elem: gtfsStore.networkMapBounds });
    return;
  }
  const lats = points.map(point => point.lat);
  const lons = points.map(point => point.lon);
  appStore.setMapFocus({
    type: MapFocusType.BOUNDS,
    elem: {
      north: Math.max(...lats) + END_FRAME_MARGIN_DEG,
      south: Math.min(...lats) - END_FRAME_MARGIN_DEG,
      east: Math.max(...lons) + END_FRAME_MARGIN_DEG,
      west: Math.min(...lons) - END_FRAME_MARGIN_DEG,
    },
  });
}

watch(
  [() => journeyStore.origin, () => journeyStore.destination],
  () => {
    if (!journeyStore.tracked) frameEnds();
  },
  { deep: true },
);

// Once only: a later position (the locate button, a silent refresh) must not steal the framing.
watch(
  () => Boolean(appStore.positionGeolocation),
  known => {
    const endsOnPosition = [journeyStore.origin, journeyStore.destination].some(
      place => place?.kind === 'position',
    );
    if (known && endsOnPosition && !journeyStore.tracked) frameEnds();
  },
);

function leaveTracking() {
  journeyStore.stopTracking();
  frameEnds();
}

function endJourney() {
  journeyStore.stopTracking();
  close();
}

function onSheetHeight(height: number, dragging: boolean) {
  appStore.setMapBottomInset(height, dragging);
}

onMounted(() => {
  journeyStore.isActive = true;
  appStore.getGeolocationIfAllowed();
  gtfsStore.setShownStops(gtfsStore.gtfsData.stops);
  gtfsStore.setShownShapes(gtfsStore.gtfsData.shapes);
  frameEnds();
});

onBeforeUnmount(() => {
  journeyStore.isActive = false;
  journeyStore.$reset();
});
</script>

<template>
  <JourneyTracking
    v-if="journeyStore.tracked"
    :tracked="journeyStore.tracked"
    :origin-label="originLabel"
    :destination-label="destinationLabel"
    @back="leaveTracking"
    @end="endJourney"
    @recalculate="journeyStore.recalculate()"
  />

  <MapPointPicker
    v-else-if="pickingOnMap"
    :end="pickingOnMap"
    @confirm="onMapPointConfirm"
    @cancel="endPickingOnMap"
  />

  <div v-else class="itinerary-page">
    <div ref="topEl" class="itinerary-page__top">
      <ItineraryField
        :origin-label="originLabel"
        :destination-label="destinationLabel"
        @pickOrigin="picking = 'origin'"
        @pickDestination="picking = 'destination'"
        @swap="swap"
        @close="close"
      />
      <button class="itinerary-page__time" type="button" @click="isTimeSelectorOpen = true">
        <BaseIcon name="clock" class="itinerary-page__time-icon" />
        {{ timeLabel }}
        <BaseIcon name="chevron-down" class="itinerary-page__time-icon" />
      </button>
    </div>

    <div class="itinerary-page__sheet-area" :style="{ top: `${topHeight + 4}px` }">
      <BottomSheet v-model:position="sheetPosition" @update:height="onSheetHeight">
        <template #header="{ scrolled }">
          <h2 class="itinerary-page__title" :class="{ 'itinerary-page__title--scrolled': scrolled }">
            {{ $t('itinerary.recommended') }}
          </h2>
        </template>

        <div class="itinerary-page__content">
          <template v-if="journeyStore.status === 'ready'">
            <JourneyCard
              v-for="journey in journeys"
              :key="journey.id"
              :journey="journey"
              :fastest="journey.id === journeyStore.results.fastestId && journeys.length > 1"
              :routes="routes"
              @select="journeyStore.startTracking(journey.id)"
            />
          </template>

          <div v-else-if="journeyStore.status === 'positionUnavailable'" class="itinerary-page__state">
            <p class="itinerary-page__state-title">{{ $t('itinerary.state.positionUnavailable') }}</p>
            <p>{{ $t('itinerary.state.positionUnavailableHint') }}</p>
            <div class="itinerary-page__state-actions">
              <ActionButton
                :label="$t('itinerary.state.chooseOrigin')"
                type="primary"
                @click="picking = 'origin'"
              />
              <ActionButton
                :label="$t('itinerary.state.enableLocation')"
                type="outline"
                @click="appStore.getGeolocation()"
              />
            </div>
          </div>

          <div v-else-if="journeyStore.status !== 'noDestination'" class="itinerary-page__state">
            <p class="itinerary-page__state-title">{{ $t(`itinerary.state.${journeyStore.status}`) }}</p>
            <p v-if="journeyStore.status === 'noJourney'">{{ $t('itinerary.state.noJourneyHint') }}</p>
          </div>
        </div>
      </BottomSheet>
    </div>

    <PlacePicker
      v-if="picking"
      :offer-position="picking === 'origin'"
      @selectStop="stopId => onPick({ kind: 'stop', stopId })"
      @selectAddress="onPickAddress"
      @selectPosition="onPick({ kind: 'position' })"
      @pickOnMap="startPickingOnMap"
      @close="picking = undefined"
    />

    <TimeSelector
      v-if="isTimeSelectorOpen"
      :mode="journeyStore.timeMode"
      :time="journeyStore.chosenTime"
      :validity="journeyStore.validity"
      @apply="onTimeApply"
      @close="isTimeSelectorOpen = false"
    />
  </div>
</template>

<style lang="scss">
.itinerary-page {
  inset: 0;
  pointer-events: none;
  position: absolute;

  &__top {
    display: flex;
    flex-direction: column;
    gap: $spacing-2;
    left: 0;
    padding: calc(12px + var(--status-bar-band)) $spacing-4 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 3;
  }

  &__time {
    align-items: center;
    align-self: flex-start;
    background-color: $color-white;
    border: 1px solid $color-border-medium;
    border-radius: $radius-pill;
    box-shadow: $shadow-soft;
    color: $color-neutral-highest;
    cursor: pointer;
    display: inline-flex;
    font: $label-small;
    gap: 6px;
    letter-spacing: $label-small-spacing;
    padding: 6px $spacing-3;
    pointer-events: auto;
  }

  &__time-icon {
    font-size: 16px;
  }

  &__sheet-area {
    bottom: 0;
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
  }

  &__title {
    border-bottom: 1px solid transparent;
    color: $color-neutral-highest;
    // Same ad-hoc large title as the home sheet.
    font: 600 24px / 32px $font-poppins;
    letter-spacing: $letter-spacing-tight;
    margin: 0;
    padding: $spacing-2 $spacing-5 $spacing-4;
    transition: border-color 0.3s ease;

    &--scrolled {
      border-color: $color-border-medium;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $spacing-4;
    padding: $spacing-5;
  }

  &__state {
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    text-align: center;

    p {
      margin: 0 0 $spacing-2;
    }
  }

  &__state-title {
    color: $color-neutral-highest;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
  }

  &__state-actions {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-2;
    justify-content: center;
    margin-top: $spacing-3;
  }
}
</style>
